import { useEffect, useRef, useState } from 'react'
import { kernel } from '@omni/kernel'
import { Slot } from '@omni/kernel/slot'
import { PaneContext } from '@omni/api'

// The canvas never uses native HTML drag-and-drop: a press on a shape is a
// pointer gesture owned by the event pipeline. Several elements drawn inside
// nodes are draggable by DEFAULT though (links, images, icon artwork, selected
// text), and once the browser starts its own drag it hijacks the gesture and
// drags that element instead of moving the shape.
//
// Cancelling dragstart at the board kills it for everything on the canvas at
// once - including node kinds added later - instead of relying on every plugin
// to remember `draggable={false}`.
//
// Dropping FILES onto the canvas still works: that gesture starts outside the
// page and fires dragenter/dragover/drop, never a dragstart from within.
const cancelNativeDrag = (e) => e.preventDefault()

// The board screen. The host renders only mounting points; every visible
// feature (canvas layers, tools, cursors...) is contributed by plugins.
export function BoardPage({ boardId }) {
  const paneRefs = useRef({})
  const [paneIds, setPaneIds] = useState(['main'])
  const [activePaneId, setActivePaneId] = useState('main')
  const [asideWidth, setAsideWidth] = useState(400)
  const [resizing, setResizing] = useState(false)
  const dragRef = useRef({ startX: 0, startWidth: 0 })
  // The camera plugin starts after this component first renders, so the first
  // pass genuinely has no camera. Re-render once it exists, or the panes keep
  // the null they were born with and nothing is ever drawn.
  //
  // And re-render EVERY time it is provided again: opening a view restarts
  // every plugin, and this page is kept (home with no view and the new view
  // are the same BoardPage). Watching only the first one left the panes
  // drawing the old camera while the wheel moved the new one - pan and zoom
  // looked dead and pen strokes landed away from the pointer until a refresh
  // (2026-09-25).
  // A new camera only asks for a render; the render reads whichever camera is
  // provided NOW, so one that went away with no replacement is not kept.
  const [, setCameraGeneration] = useState(0)
  useEffect(() => {
    const d = kernel.services.whenAvailable('omni.camera', () => setCameraGeneration((n) => n + 1))
    return () => d?.dispose?.()
  }, [])

  // Refs so the stable service always reads current values
  const paneIdsRef = useRef(paneIds)
  const activePaneIdRef = useRef(activePaneId)
  const asideWidthRef = useRef(asideWidth)
  paneIdsRef.current = paneIds
  activePaneIdRef.current = activePaneId
  asideWidthRef.current = asideWidth

  // Subscribers for the notify function
  const subscribersRef = useRef(new Set())

  // Stable panesService created once; methods read from refs above
  const [panesService] = useState(() => ({
    list: () => paneIdsRef.current,
    active: () => activePaneIdRef.current,
    setActive: (id) => {
      if (paneIdsRef.current.includes(id)) {
        setActivePaneId(id)
      }
    },
    isOpen: (id) => paneIdsRef.current.includes(id),
    open: (id) => {
      if (id === 'aside' && !paneIdsRef.current.includes('aside')) {
        setPaneIds(['main', 'aside'])
      }
    },
    close: (id) => {
      if (id === 'aside' && paneIdsRef.current.includes('aside')) {
        setPaneIds(['main'])
        if (activePaneIdRef.current === 'aside') {
          setActivePaneId('main')
        }
      }
    },
    getWidth: (id) => (id === 'aside' ? asideWidthRef.current : null),
    setWidth: (id, px) => {
      if (id === 'aside') {
        const maxWidth = Math.floor(window.innerWidth * 0.8)
        setAsideWidth(Math.max(200, Math.min(px, maxWidth)))
      }
    },
    elOf: (id) => paneRefs.current[id] ?? null,
    subscribe: (cb) => {
      subscribersRef.current.add(cb)
      return { dispose: () => subscribersRef.current.delete(cb) }
    },
  }))

  // Notify subscribers when pane state changes
  const notify = () => {
    for (const cb of subscribersRef.current) cb()
  }

  const handlePointerDown = (e) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { startX: e.clientX, startWidth: asideWidthRef.current }
    setResizing(true)
  }

  const handlePointerMove = (e) => {
    if (!resizing) return
    const dx = e.clientX - dragRef.current.startX
    // aside is on the RIGHT, moving left makes it wider
    panesService.setWidth('aside', dragRef.current.startWidth - dx)
  }

  // pointerup and pointercancel both end the drag
  const handlePointerEnd = (e) => {
    if (!resizing) return
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    setResizing(false)
    dragRef.current = { startX: 0, startWidth: 0 }
  }

  useEffect(() => {
    notify()
  }, [paneIds, activePaneId, asideWidth])

  // Provide the omni.panes service (stable reference)
  useEffect(() => {
    const disposable = kernel.services.provide('omni.panes', panesService)
    return () => disposable.dispose()
  }, [panesService])

  // Give the pipeline the activation setter so a press sets the active pane
  useEffect(() => {
    const disposable = kernel.events.setActivePaneSetter((paneId) => {
      setActivePaneId(paneId)
    })
    return () => disposable.dispose()
  }, [])

  // Attach/detach panes to the event pipeline
  useEffect(() => {
    for (const paneId of paneIds) {
      const el = paneRefs.current[paneId]
      if (el) {
        kernel.events.attach(el, paneId)
      }
    }
    return () => {
      for (const paneId of paneIds) {
        kernel.events.detachPane(paneId)
      }
    }
  }, [paneIds])

  // Render a single pane
  const renderPane = (paneId) => {
    const isActive = paneId === activePaneId
    const paneEl = paneRefs.current[paneId]
    // getNow, NOT get: services.get returns a PROMISE, and a promise has no
    // forPane, so this was null on every render and every pane's world layer
    // drew nothing. The board was blank and the document was fine, which is
    // why hit-testing still worked and the checks still passed.
    const camera =
      kernel.services.getNow('omni.camera')?.forPane?.(paneId) ?? null

    return (
      <PaneContext.Provider
        key={paneId}
        value={{
          paneId,
          isActive,
          el: paneEl,
          camera,
        }}
      >
        <div
          ref={(el) => (paneRefs.current[paneId] = el)}
          className={`${
            paneId === 'main'
              // The main pane is the one at the window's left edge, which the
              // tool bar also uses in landscape. Its bottom-left chrome (the
              // pane bar, the minimap) starts right of the tool bar column:
              // on a short window the centred tool bar reaches the bottom and
              // the minimap sat over its last buttons (2026-09-25).
              ? 'relative h-full min-w-0 flex-1 landscape:[--omni-pane-left-inset:4rem]'
              : 'relative h-full shrink-0'
          }${paneId === 'aside' || (paneId === 'main' && !paneIds.includes('aside')) ? ' [--omni-right-inset:var(--omni-browser-inset,0px)]' : ''}`}
          style={{
            touchAction: 'none',
            background: 'var(--omni-board-bg, #f8f9fa)',
            ...(paneId === 'aside' ? { width: `${asideWidth}px` } : {}),
            ...(resizing ? { pointerEvents: 'none' } : {}),
          }}
          data-board-pane={paneId}
          role="application"
          aria-label={paneId === 'aside' ? 'Second board view: double-click to write' : 'Board: double-click to write'}
          data-pane-has-camera={camera ? '1' : '0'}
          data-board={isActive ? boardId ?? 'none' : undefined}
          data-board-inactive={!isActive ? '' : undefined}
          onDragStart={cancelNativeDrag}
        >
          <Slot name="canvas.root" registry={kernel.slots} className="h-full w-full" />
          {isActive && <Slot name="canvas.screen" registry={kernel.slots} className="pointer-events-none absolute inset-0" />}
          {paneId !== 'main' && (
            <Slot
              name="pane.statusbar"
              registry={kernel.slots}
              // Bottom-RIGHT of its own pane, above the dock on tall phones. The
              // main pane's zoom lives in the shared bottom bar (Vu 2026-10-19);
              // the aside pane keeps this corner bar.
              className="pointer-events-auto absolute bottom-2 right-[calc(0.5rem+var(--omni-right-inset,0px))] z-10 flex items-center gap-2 rounded-lg border border-base-300 bg-base-100 px-2 py-1 text-xs shadow portrait:bottom-14"
            />
          )}
        </div>
      </PaneContext.Provider>
    )
  }

  // overflow-clip, not hidden: a hidden box can still be scrolled by the browser (a playing video frame scrolled the board blank, 2026-10-12).
  return (
    <div className="bg-base-200 relative flex h-full w-full overflow-clip portrait:[--omni-dock-lift:3rem] [--omni-bar-top:60px] max-md:[--omni-bar-top:104px]">
      {/* chrome overlays; the top bar is z-30, above the tool bar, because the main menu drops over it (left and right pills) */}
      <div data-omni-chrome="topbar" className="pointer-events-none absolute inset-x-0 top-0 z-30 flex items-center gap-2 p-2">
        <Slot
          name="topbar"
          registry={kernel.slots}
          className="bg-base-100 pointer-events-auto flex items-center gap-1 rounded-lg p-1 shadow-sm empty:hidden"
        />
        <Slot
          name="topbar-end"
          registry={kernel.slots}
          className="bg-base-100 pointer-events-auto flex items-center gap-1 rounded-lg p-1 shadow-sm empty:hidden ml-auto"
        />
      </div>

      <Slot
        name="toolbar"
        registry={kernel.slots}
        className="bg-base-100 pointer-events-auto absolute z-20 flex gap-1 rounded-lg p-1 shadow portrait:bottom-3 portrait:left-1/2 portrait:-translate-x-1/2 portrait:flex-row landscape:top-1/2 landscape:left-3 landscape:-translate-y-1/2 landscape:flex-col"
      />

      <Slot
        name="sidebar"
        registry={kernel.slots}
        className="pointer-events-auto absolute top-16 right-3 z-20 flex flex-col items-end gap-2"
      />

      {/* One bar: chat, status chips, zoom (Vu 2026-10-19). The minimap floats
          just above it. The main pane's zoom chips sit here; the aside pane
          keeps its own corner bar. The bar is marked keep-clear: the style bar
          lifts above it (2026-10-20, it painted over it).
          It is z-20 like the other chrome: without it the panes, later in the page, hid its box and the zoom chip (2026-10-20). */}
      <div
        data-test="bottom-dock"
        className="pointer-events-none absolute bottom-0 left-0 z-20 pl-3 pr-3 pt-10 pb-2 landscape:pl-16 portrait:pb-14"
        style={{ right: paneIds.includes('aside') ? asideWidth + 12 : 'var(--omni-browser-inset, 0px)' }}
      >
        <div
          data-test="bottom-bar"
          data-omni-keep-clear=""
          className="pointer-events-auto flex min-h-11 items-center gap-2 rounded-box border border-base-300 bg-base-100 px-1.5 py-1 shadow max-md:flex-wrap"
        >
          <Slot
            name="dock-start"
            registry={kernel.slots}
            className="relative z-30 flex h-9 w-[min(36rem,50%)] min-w-0 shrink items-center max-md:w-full"
          />
          <Slot
            name="statusbar"
            registry={kernel.slots}
            className="relative z-20 flex min-w-0 flex-1 items-center justify-center gap-2 text-xs"
          />
          <PaneContext.Provider
            value={{
              paneId: 'main',
              isActive: activePaneId === 'main',
              el: paneRefs.current.main,
              camera: kernel.services.getNow('omni.camera')?.forPane?.('main') ?? null,
            }}
          >
            <Slot name="pane.statusbar" registry={kernel.slots} className="flex items-center gap-2 text-xs" />
          </PaneContext.Provider>
        </div>
      </div>

      {/* Panes in a flex row. AFTER the chrome in the page, so Tab goes top
          bar, toolbar, panels, then the board: first it went to the board's
          middle, then the corners, then the top bar (2026-09-26 new-user
          test, #65). What sits on top is decided by z-index, not by order:
          the chrome is z-20, above the panes' own z-10 minimap and zoom bar,
          which after the move covered the toolbar's More menu (retest). */}
      <div className="absolute inset-0 flex h-full w-full overflow-hidden">
        {/* One flat list - main, divider, aside - so opening the second view
            keeps the main pane mounted (a Fragment in its place remounted it). */}
        {paneIds.flatMap((paneId) => paneId !== 'aside' ? [renderPane(paneId)] : [
          <div
            key="divider"
            data-test="pane-divider"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the two views"
            className="group relative z-10 flex h-full w-3 shrink-0 cursor-col-resize items-center justify-center border-x border-base-300 bg-base-200"
            style={{ touchAction: 'none' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
          >
            <div
              data-test="pane-divider-bar"
              className={`flex h-full w-1 items-center justify-center transition-colors group-hover:bg-primary ${resizing ? 'bg-primary' : 'bg-base-content/20'}`}
            >
            </div>
          </div>,
          renderPane(paneId),
        ])}
      </div>
    </div>
  )
}
